/**
 * Web Audio API ベースの音声再生composable
 * iOS/iPadOSのWeb音声再生制限を回避するためのアンロック機構を実装
 */
import { ref } from '@vue/composition-api'

// 通知音の定義
const NOTIFICATION_SOUNDS = {
  newOrder: '/sounds/new-order.mp3',
  checkRequest: '/sounds/check-request.mp3',
}

// AudioContextのシングルトン
let audioContext = null
let isUnlocked = false
const audioBuffers = {}
const isReady = ref(false)

/**
 * AudioContextを取得または作成
 */
const getAudioContext = () => {
  if (!audioContext) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (AudioContextClass) {
      audioContext = new AudioContextClass()
    }
  }

  return audioContext
}

/**
 * iOS向けAudioContextアンロック処理
 * ユーザーインタラクション時に無音を再生してAudioContextを有効化
 */
const unlockAudio = async () => {
  if (isUnlocked) return true

  const ctx = getAudioContext()
  if (!ctx) {
    console.warn('[AudioPlayer] AudioContext not supported')

    return false
  }

  try {
    // suspendedの場合はresumeを試みる
    if (ctx.state === 'suspended') {
      await ctx.resume()
    }

    // 無音バッファを再生してアンロック
    const buffer = ctx.createBuffer(1, 1, 22050)
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.connect(ctx.destination)
    source.start(0)

    isUnlocked = true

    return true
  } catch (err) {
    console.warn('[AudioPlayer] Failed to unlock audio:', err)

    return false
  }
}

/**
 * 音声ファイルをプリロード
 */
const preloadSounds = async () => {
  const ctx = getAudioContext()
  if (!ctx) return

  const loadSound = async (name, url) => {
    try {
      const response = await fetch(url)
      if (!response.ok) {
        console.warn(`[AudioPlayer] Failed to fetch ${name}: ${response.status}`)

        return
      }
      const arrayBuffer = await response.arrayBuffer()
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer)
      audioBuffers[name] = audioBuffer
    } catch (err) {
      console.warn(`[AudioPlayer] Failed to preload ${name}:`, err)
    }
  }

  await Promise.all(
    Object.entries(NOTIFICATION_SOUNDS).map(([name, url]) => loadSound(name, url)),
  )

  isReady.value = true
}

/**
 * 音声を再生
 */
const playSound = async soundType => {
  const ctx = getAudioContext()
  if (!ctx) {
    console.warn('[AudioPlayer] AudioContext not available')

    return false
  }

  // まだアンロックされていない場合はアンロックを試みる
  if (!isUnlocked) {
    await unlockAudio()
  }

  // AudioContextがsuspendedの場合はresumeを試みる
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume()
    } catch (err) {
      console.warn('[AudioPlayer] Failed to resume AudioContext:', err)
    }
  }

  // プリロードされたバッファを使用
  const buffer = audioBuffers[soundType]
  if (buffer) {
    try {
      const source = ctx.createBufferSource()
      source.buffer = buffer

      // ゲインノードでボリューム調整
      const gainNode = ctx.createGain()
      gainNode.gain.value = 0.7

      source.connect(gainNode)
      gainNode.connect(ctx.destination)
      source.start(0)

      return true
    } catch (err) {
      console.warn(`[AudioPlayer] Failed to play ${soundType}:`, err)
    }
  }

  // バッファがない場合はフォールバックとしてHTMLAudioElementを使用
  const soundPath = NOTIFICATION_SOUNDS[soundType]
  if (soundPath) {
    try {
      const audio = new Audio(soundPath)
      audio.volume = 0.7
      await audio.play()

      return true
    } catch (err) {
      console.warn(`[AudioPlayer] Fallback play failed for ${soundType}:`, err)
    }
  }

  return false
}

/**
 * アンロック用のイベントリスナーを設定
 * ユーザーの最初のタッチ/クリックでAudioContextをアンロック
 */
const setupUnlockListeners = () => {
  const unlockEvents = ['touchstart', 'touchend', 'click', 'keydown']

  const handleUnlock = async () => {
    if (isUnlocked) return

    const success = await unlockAudio()
    if (success) {
      // アンロック成功後はリスナーを削除
      unlockEvents.forEach(event => {
        document.removeEventListener(event, handleUnlock, true)
      })
    }
  }

  unlockEvents.forEach(event => {
    document.addEventListener(event, handleUnlock, true)
  })
}

export default function useAudioPlayer() {
  return {
    isReady,
    getAudioContext,
    unlockAudio,
    preloadSounds,
    playSound,
    setupUnlockListeners,
  }
}
